import {
    HttpContextToken,
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, TimeoutError, retry, tap, throwError, timer, timeout } from 'rxjs';
import { BackendAvailabilityService } from '../reliability/backend-availability.service';

/** Explicit opt-in for a mutation that carries a server-enforced Idempotency-Key header. */
export const HTTP_IDEMPOTENT_REQUEST = new HttpContextToken<boolean>(() => false);
/** Opt-out for downloads or other deliberately long-running GET requests. */
export const HTTP_RELIABILITY_DISABLED = new HttpContextToken<boolean>(() => false);

const TRANSIENT_HTTP_STATUSES = new Set([0, 408, 429, 502, 503, 504]);

export const httpReliabilityInterceptor = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
    if (request.context.get(HTTP_RELIABILITY_DISABLED) || request.url.includes('/auth/')) {
        return next(request);
    }

    const availability = inject(BackendAvailabilityService);
    const service = resolveService(request.url);
    const readOnly = request.method === 'GET' || request.method === 'HEAD';
    const idempotentWrite = request.context.get(HTTP_IDEMPOTENT_REQUEST)
        && request.headers.has('Idempotency-Key');
    const retryableRequest = readOnly || idempotentWrite;

    let source = next(request).pipe(
        tap({ next: () => availability.markAvailable(service) }),
    );
    if (!retryableRequest) {
        return source.pipe(catchError((error) => {
            if (isTransient(error)) availability.markUnavailable(service);
            else availability.markAvailable(service);
            return throwError(() => error);
        }));
    }

    source = source.pipe(timeout(15_000));
    return source.pipe(
        retry({
            count: 2,
            delay: (error, retryCount) => {
                const transient = isTransient(error);
                if (!transient || !availability.canAutoRetry(service)) {
                    return throwError(() => error);
                }
                availability.markRetrying(service);
                return timer(httpRetryDelay(error, retryCount));
            },
        }),
        catchError((error) => {
            if (isTransient(error)) availability.markUnavailable(service);
            else availability.markAvailable(service);
            return throwError(() => error);
        }),
    );
};

function isTransient(error: unknown): boolean {
    return error instanceof TimeoutError
        || error instanceof HttpErrorResponse && TRANSIENT_HTTP_STATUSES.has(error.status);
}

function httpRetryDelay(error: unknown, retryCount: number): number {
    if (error instanceof HttpErrorResponse) {
        const raw = error.headers.get('Retry-After');
        if (raw) {
            const seconds = Number(raw);
            if (Number.isFinite(seconds) && seconds >= 0) return Math.min(seconds * 1000, 30_000);
            const at = Date.parse(raw);
            if (Number.isFinite(at)) return Math.max(0, Math.min(at - Date.now(), 30_000));
        }
    }
    const base = retryCount <= 1 ? 500 : 1_500;
    return Math.round(base * (0.8 + Math.random() * 0.4));
}

function resolveService(url: string): string {
    try {
        const parsed = new URL(url, document.baseURI);
        const segments = parsed.pathname.split('/').filter(Boolean);
        return `rest:${segments.slice(0, 2).join('/') || parsed.host}`;
    } catch {
        return 'rest:unknown';
    }
}
